import { useCallback } from 'react';
import { contractsService } from '../services';
import {
  useResourceCollection,
  useResourceById,
  useCreateResource,
  useUpdateResource,
  useDeleteResource,
  ResourceState,
  SingleResourceState,
  MutationState,
} from './useResource';
import { ResourceQueryOptions } from '../services/resources.service';
import { Contract } from '../types';

export function useContracts(
  options?: ResourceQueryOptions
): ResourceState<Contract> & { meta?: any } {
  const fetchFn = useCallback(
    (opts?: ResourceQueryOptions) =>
      contractsService.getAll(opts) as unknown as Promise<{ data: Contract[]; meta?: any }>,
    []
  );
  return useResourceCollection<Contract>(fetchFn, options);
}

export function useContract(
  id: string | null,
  options?: ResourceQueryOptions
): SingleResourceState<Contract> {
  const numericId = id ? parseInt(id, 10) : null;
  const fetchFn = useCallback(
    (contractId: number, opts?: ResourceQueryOptions) =>
      contractsService.getById(contractId, opts) as unknown as Promise<Contract>,
    []
  );
  return useResourceById<Contract>(fetchFn, numericId, options);
}

export function useCreateContract(
  onSuccess?: (data: Contract) => void
): MutationState<Contract> {
  const createFn = useCallback(
    (data: any) => contractsService.create(data) as unknown as Promise<Contract>,
    []
  );
  return useCreateResource<Contract>(createFn, onSuccess);
}

export function useUpdateContract(
  onSuccess?: (data: Contract) => void
): MutationState<Contract> {
  const updateFn = useCallback(
    (id: string | number, data: any) => {
      const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
      return contractsService.update(numericId, data) as unknown as Promise<Contract>;
    },
    []
  );
  return useUpdateResource<Contract>(updateFn, onSuccess);
}

/**
 * Delete contract using documentId
 * @param data - Full contract object containing documentId
 */
export function useDeleteContract(onSuccess?: () => void): MutationState<void> {
  const deleteFn = useCallback(
    (data: any) => {
      const documentId = data.documentId || data.id?.toString() || data.id;
      return contractsService.delete(documentId) as unknown as Promise<void>;
    },
    []
  );
  return useDeleteResource(deleteFn, onSuccess);
}
// Auto-generated hook file
